import fs from "node:fs/promises";
import path from "node:path";
import { exists, readJson, readJsonc } from "../../core/filesystem.js";
import { OpenCodeAdapter } from "../../core/runtime/opencode-adapter.js";

const REQUIRED_FILES = [
  ".ai-workflow",
  "opencode/README.md"
];

async function isSymlink(filePath: string): Promise<boolean> {
  try {
    const stat = await fs.lstat(filePath);
    return stat.isSymbolicLink();
  } catch {
    return false;
  }
}

export interface DoctorOptions {
  cwd: string;
}

interface DoctorState {
  hasFailure: boolean;
  hasWarning: boolean;
  managedBlocks: string[];
  runtimes: string[];
}

async function checkOpenCodeRuntime(cwd: string, state: DoctorState): Promise<void> {
  const adapter = new OpenCodeAdapter({ cwd });
  const inspection = await adapter.inspect();
  if (inspection.available) {
    console.log("PASS opencode CLI is available");
    const supportedStr = Object.entries(inspection.supports)
      .filter(([_, v]) => v)
      .map(([k, _]) => k)
      .join(", ");
    console.log(`PASS opencode capabilities detected: ${supportedStr || "none"}`);
    if (inspection.supports.agent) {
      console.log("PASS opencode --agent selection is supported");
    } else {
      state.hasWarning = true;
      console.log("WARN opencode --agent selection is not supported; prompt fallback is available");
    }
  } else {
    state.hasFailure = true;
    console.log("FAIL opencode CLI is not installed or not available in PATH");
  }
}

async function checkRequiredFiles(cwd: string, state: DoctorState): Promise<void> {
  for (const relativePath of REQUIRED_FILES) {
    const ok = await exists(path.join(cwd, relativePath));
    if (ok) {
      console.log(`PASS ${relativePath}`);
    } else {
      state.hasFailure = true;
      console.log(`FAIL ${relativePath} missing`);
    }
  }
}

async function checkAiWorkflowConfig(cwd: string, state: DoctorState): Promise<void> {
  const configPathV2 = path.join(cwd, ".ai-workflow/config.json");
  const configPathLegacy = path.join(cwd, ".ai-workflow.json");
  let configPath: string | null = null;

  if (await exists(configPathV2)) {
    configPath = configPathV2;
  } else if (await exists(configPathLegacy)) {
    configPath = configPathLegacy;
  }

  if (!configPath) return;

  try {
    const config = await readJson(configPath);
    const basename = path.basename(configPath);

    if (config.installMode === "project-local" || config.mode === "standalone") {
      console.log("PASS install mode is project-local/standalone");
    } else {
      state.hasWarning = true;
      console.log(`WARN install mode is not project-local/standalone in ${basename}`);
    }

    if (config.profile) {
      console.log(`PASS profile detected: ${config.profile}`);
    } else {
      state.hasWarning = true;
      console.log(`WARN profile is missing in ${basename}`);
    }

    if (config.runtime) {
      console.log(`PASS runtime: ${config.runtime}`);
      state.runtimes = config.runtime.split("+");
    } else {
      console.log(`NOTE runtime field not present in ${basename}`);
      state.runtimes = ["opencode"];
    }

    if (Array.isArray(config.managedBlocks)) {
      state.managedBlocks = config.managedBlocks;
    } else {
      console.log(`NOTE managedBlocks not present in ${basename} (legacy field)`);
    }

    if (Array.isArray(config.managedFiles)) {
      for (const relativePath of config.managedFiles) {
        const fileExists = await exists(path.join(cwd, relativePath));
        if (!fileExists) {
          state.hasWarning = true;
          console.log(`WARN managed file missing: ${relativePath}`);
        }
      }
    }

    if (Array.isArray(config.managedLinks)) {
      for (const relativePath of config.managedLinks) {
        const absolutePath = path.join(cwd, relativePath);
        if (!(await exists(absolutePath))) {
          state.hasWarning = true;
          console.log(`WARN managed link missing: ${relativePath}`);
          continue;
        }
        if (!(await isSymlink(absolutePath))) {
          state.hasWarning = true;
          console.log(`WARN managed link is not a symlink: ${relativePath}`);
        }
      }
    }
  } catch {
    state.hasFailure = true;
    console.log(`FAIL ${path.basename(configPath)} is not valid JSON`);
  }
}

async function checkOpencodeJson(cwd: string, state: DoctorState): Promise<void> {
  const opencodePath = path.join(cwd, "opencode.jsonc");
  if (!(await exists(opencodePath))) {
    state.hasFailure = true;
    console.log("FAIL opencode.jsonc missing");
    return;
  }
  
  try {
    const opencodeConfig = await readJsonc(opencodePath);
    console.log("PASS opencode.jsonc is valid JSONC");

    const needsAgentDefault = state.managedBlocks.includes("opencode.jsonc:agent.default");
    if (!needsAgentDefault || opencodeConfig.agent?.default) {
      console.log("PASS opencode agent.default available");
    } else {
      state.hasFailure = true;
      console.log("FAIL opencode agent.default missing");
    }

    const requiredCommands = state.managedBlocks
      .filter((block) => block.startsWith("opencode.jsonc:command."))
      .map((block) => block.replace("opencode.jsonc:command.", ""));

    if (requiredCommands.length === 0) {
      console.log("PASS no managed opencode commands required");
    } else {
      const missingCommands = requiredCommands.filter((command) => !opencodeConfig.command?.[command]);
      if (missingCommands.length === 0) {
        console.log(`PASS opencode managed commands available (${requiredCommands.length})`);
      } else {
        state.hasFailure = true;
        console.log(`FAIL opencode command entries missing: ${missingCommands.join(", ")}`);
      }
    }

    const requiredAgents = state.managedBlocks
      .filter((block) => block.startsWith("opencode.jsonc:agent.") && !block.endsWith(".default"))
      .map((block) => block.replace("opencode.jsonc:agent.", ""));

    if (requiredAgents.length === 0) {
      console.log("PASS no managed opencode agents required");
    } else {
      const missingAgents = requiredAgents.filter((agent) => !opencodeConfig.agent?.[agent]);
      if (missingAgents.length === 0) {
        console.log(`PASS opencode managed agents available (${requiredAgents.length})`);
      } else {
        state.hasFailure = true;
        console.log(`FAIL opencode agent entries missing: ${missingAgents.join(", ")}`);
      }
    }
  } catch {
    state.hasFailure = true;
    console.log("FAIL opencode.jsonc is not valid JSONC");
  }
}

async function checkPackageJson(cwd: string, state: DoctorState): Promise<void> {
  const packageJsonPath = path.join(cwd, "package.json");
  if (await exists(packageJsonPath)) {
    try {
      const packageJson = await readJson(packageJsonPath);
      const scripts = packageJson.scripts ?? {};

      if (!scripts.validate) {
        state.hasWarning = true;
        console.log("WARN package.json has no validate script");
      }
    } catch {
      state.hasFailure = true;
      console.log("FAIL package.json is not valid JSON");
    }
  }
}

async function checkCodexRuntime(cwd: string, state: DoctorState): Promise<void> {
  const checks = [
    { name: "root AGENTS.md exists", path: "AGENTS.md" },
    { name: ".codex/skills exists", path: ".codex/skills" },
    { name: ".codex prompts detected", path: ".codex/prompts" },
    { name: ".codex policies detected", path: ".codex/docs/policies" }
  ];

  for (const check of checks) {
    const ok = await exists(path.join(cwd, check.path));
    if (ok) {
      console.log(`PASS ${check.name}`);
    } else {
      state.hasFailure = true;
      console.log(`FAIL ${check.name}`);
    }
  }

  // Also check if CODEX.md exists
  const codexMdOk = await exists(path.join(cwd, "CODEX.md"));
  if (codexMdOk) {
    console.log(`PASS root CODEX.md exists`);
  }

  if (state.runtimes.includes("codex")) {
    console.log("PASS config runtime includes codex");
  } else {
    state.hasFailure = true;
    console.log("FAIL config runtime includes codex");
  }
}

export async function runDoctor({ cwd }: DoctorOptions): Promise<void> {
  const state: DoctorState = {
    hasFailure: false,
    hasWarning: false,
    managedBlocks: [],
    runtimes: ["opencode"]
  };

  console.log("Doctor report:");

  await checkRequiredFiles(cwd, state);
  await checkAiWorkflowConfig(cwd, state);

  if (state.runtimes.includes("opencode")) {
    await checkOpenCodeRuntime(cwd, state);
    await checkOpencodeJson(cwd, state);
  }

  if (state.runtimes.includes("codex")) {
    await checkCodexRuntime(cwd, state);
  }

  await checkPackageJson(cwd, state);

  const finalStatus = state.hasFailure ? "FAIL" : state.hasWarning ? "PASS_WITH_NOTES" : "PASS";
  console.log(`Final status: ${finalStatus}`);

  if (state.hasFailure) {
    process.exitCode = 1;
  }
}

